/**
 * Liver Score — meal scoring + score context. Web copy (1.5.0).
 * LINE-IDENTICAL with functions/src/utils/scoring.ts (server) and
 * services/liver/scoring.ts (mobile) below the IMPORTS-END marker;
 * functions/test/scoringParity.test.js asserts it. Edit all three together.
 * The web uses it to re-score a meal after a food edit (log/rescore.ts) — the
 * same result the server's Phase 2 computes for those foods.
 */

import { scoreFromNutrients } from './healthScore';
import type { DailyLimits, RiskLevel, ScoreBreakdown, ScoreDriver } from './healthScore';
import { isAlcoholFreeName, isCookedOffName, nameLooksAlcoholic } from '@/lib/alcoholPatterns';

// IMPORTS-END (everything below this line is asserted identical across copies)

/*
 * ONE meal scorer shared by the server (which persists scores) and the clients
 * (which re-score a meal after a food edit). Nothing here changes the Liver
 * Score: it is the server's Phase 2 (phase2PostProcess.ts) factored out so the
 * app and the web compute exactly what the server would for the same foods.
 *
 *   1. Meal totals: raw per-serving food values summed in `foods` order with
 *      `value || 0` (no rounding before scoring).
 *   2. Headline: scoreFromNutrients(totals, the user's daily budgets).
 *   3. Per-food badge: the same engine on each food alone, so a badge can
 *      never contradict the headline; `primaryConcern` is the driver token.
 *   4. Energy-drink override (spec §4): an energy drink floors the meal at 45
 *      (driver 'caffeine') and lifts a 'safe' badge to 'moderate'.
 *   5. Alcohol override (spec §4): any alcoholic item (or alcohol in a label's
 *      ingredient list) floors the meal at 90 (driver 'alcohol', badge 'limit').
 *      Runs after the energy-drink floor, so alcohol always wins.
 */

/** Meal score floor for any alcoholic item: there is no safe amount with liver disease. */
export const ALCOHOL_SCORE_FLOOR = 90;
/** Meal score floor for any energy drink (mid-moderate; sugared ones reach 90+ on sugar). */
export const ENERGY_DRINK_SCORE_FLOOR = 45;

export type FoodSafety = 'safe' | 'moderate' | 'limit';
export type LimitSource = 'calculatedLimits' | 'prevention' | 'fattyLiverDefault';

/** RiskLevel → stored SafetyRating ('high' is stored as 'limit'). */
export function riskToSafety(risk: RiskLevel): FoodSafety {
  return risk === 'high' ? 'limit' : risk;
}

// ── Energy drinks (spec §4) ─────────────────────────────────────────────────

/**
 * ENERGY-DRINK detection — the nutrient axes cannot see stimulant + additive
 * load (caffeine 80-300mg/can, niacin/taurine doses), so a zero-sugar energy
 * drink scores ~5/safe on nutrients alone and the green reads as an
 * endorsement. Matched against AI-returned food names (user's language) —
 * multilingual generic terms + unambiguous brand names only. Brand words that
 * collide with real dishes ("bang bang chicken", "monster cookie") are guarded.
 */
export const ENERGY_DRINK_PATTERNS: RegExp[] = [
  // Generic category names across the app's locales
  /energy\s*drink/i, //            en (+ loanword in many locales)
  /energy\s*shot/i,
  /energydrink/i, //               de/nl compound
  /energiegetr(ä|a)nk/i, //    de
  /bebida\s+energ(é|e)tica/i, // es/pt
  /\benerg(é|e)tico\b/i, //    pt-BR colloquial ("um energético")
  /boisson\s+(é|e)nergisante/i, // fr
  /bevanda\s+energetica/i, //      it
  /energiedrank/i, //              nl
  /nap(ó|o)j\s+energetyczny/i, // pl
  /enerji\s+i(ç|c)ece(ğ|g)i/i, // tr
  /エナジードリンク|エナジー飲料/, //    ja
  /에너지\s*드링크|에너지\s*음료/, //   ko
  /能量饮料|能量飲料|提神飲料/, //       zh
  /مشروب\s*(ال)?طاقة/, //          ar
  // Unambiguous brands (guarded where a bare word collides with dish names)
  /red\s*bull/i,
  /\bmonster\s+(energy|ultra|juice|rehab)\b/i, // NOT "monster cookie"
  /\brockstar\s+(energy|punched|recovery)\b/i,
  /\bcelsius\b/i, //              the drink — degrees never appear as a food name
  /\breign\s+(energy|storm|total)\b/i,
  /\bbang\s+energy\b/i, //        NOT "bang bang chicken/shrimp"
  /5[\s-]?hour\s+energy/i,
  /\bnos\s+energy\b/i,
  /\bfull\s+throttle\b/i,
  /\bg\s?fuel\b/i,
  /\bghost\s+energy\b/i,
  /\bprime\s+energy\b/i, //       NOT Prime Hydration (a sports drink)
  /\bc4\s+(energy|smart)\b/i,
  /\balani\s+(nu|energy)\b/i,
  /\beffect\s+energy\b/i, //      de brand
  /\bbooster\s+energy\b/i, //     de supermarket brand
];

/**
 * Coffee/tea guard — coffee is a celebrated positive in the liver model
 * (2-3 cups; spec §8), so neither the name patterns nor an AI isEnergyDrink
 * mis-flag may ever floor a coffee or tea drink. Checked before the patterns
 * AND before honoring the AI flag.
 */
export const NOT_ENERGY_DRINK_PATTERNS: RegExp[] = [
  /coffee|espresso|latte|cappuccino|americano|macchiato|mocha|cold\s*brew/i,
  /kaffee|caf(é|è)|koffie|\bkawa\b|kahve/i, //  de/fr-es-pt/nl/pl/tr
  /コーヒー|커피|咖啡|قهوة/, //        ja/ko/zh/ar
  /\btea\b|\btee\b|\bt(é|è)\b|\bchai\b|茶|녹차|홍차|شاي/i,
  /energy\s+(bar|ball|bite|gel)\b/i, // solid "energy" foods are not drinks
];

/** True when the name is guarded from the energy-drink override (coffee, tea, energy bars). */
export function isEnergyDrinkGuardedName(name: string | null | undefined): boolean {
  return !!name && NOT_ENERGY_DRINK_PATTERNS.some((p) => p.test(name));
}

/** True when the NAME alone makes the food an energy drink (guard applied). */
export function nameLooksLikeEnergyDrink(name: string | null | undefined): boolean {
  if (!name) return false;
  if (isEnergyDrinkGuardedName(name)) return false;
  return ENERGY_DRINK_PATTERNS.some((p) => p.test(name));
}

/** Indices of foods whose names match an energy-drink pattern. */
export function findEnergyDrinkIndices(names: (string | null | undefined)[]): number[] {
  const indices: number[] = [];
  names.forEach((name, i) => {
    if (nameLooksLikeEnergyDrink(name)) indices.push(i);
  });
  return indices;
}

/** Indices of foods whose names match an alcohol pattern (./alcoholPatterns). */
export function findAlcoholFoodIndices(names: (string | null | undefined)[]): number[] {
  const indices: number[] = [];
  names.forEach((name, i) => {
    if (nameLooksAlcoholic(name)) indices.push(i);
  });
  return indices;
}

/** A food as the analyzers see it: name plus the model's defense-in-depth flags. */
export interface FlaggableFood {
  name?: string | null;
  isAlcoholic?: boolean | null;
  isEnergyDrink?: boolean | null;
}

/**
 * The server's alcohol rule for one analyzed food: the name matches, or the
 * model flagged it — unless the name is alcohol-free ("0.0%", "virgin") or a
 * cooked dish ("coq au vin", "beer-battered fish"), which no flag overrides.
 */
export function foodTriggersAlcohol(f: FlaggableFood): boolean {
  if (nameLooksAlcoholic(f.name)) return true;
  if (f.isAlcoholic !== true) return false;
  return !(f.name && (isAlcoholFreeName(f.name) || isCookedOffName(f.name)));
}

/** The server's energy-drink rule for one analyzed food (coffee / tea guard first). */
export function foodTriggersEnergyDrink(f: FlaggableFood): boolean {
  if (nameLooksLikeEnergyDrink(f.name)) return true;
  if (f.isEnergyDrink !== true) return false;
  return !isEnergyDrinkGuardedName(f.name);
}

// ── Meal scoring ────────────────────────────────────────────────────────────

/** Anything carrying per-serving scored nutrients (identified food, stored food). */
export interface ScorableFood {
  name?: string | null;
  calories?: number | null;
  addedSugarsG?: number | null;
  saturatedFatG?: number | null;
  sodiumMg?: number | null;
  fiberG?: number | null;
  proteinG?: number | null;
}

/**
 * Meal totals for scoring: raw per-serving values summed in `foods` order,
 * `value || 0` — the same reduction as the server's calculateTotalsFromFoods.
 */
export function sumScoredNutrients(foods: readonly ScorableFood[]): {
  addedSugarsG: number;
  saturatedFatG: number;
  calories: number;
  sodiumMg: number;
  fiberG: number;
  proteinG: number;
} {
  return {
    addedSugarsG: foods.reduce((sum, f) => sum + (f.addedSugarsG || 0), 0),
    saturatedFatG: foods.reduce((sum, f) => sum + (f.saturatedFatG || 0), 0),
    calories: foods.reduce((sum, f) => sum + (f.calories || 0), 0),
    sodiumMg: foods.reduce((sum, f) => sum + (f.sodiumMg || 0), 0),
    fiberG: foods.reduce((sum, f) => sum + (f.fiberG || 0), 0),
    proteinG: foods.reduce((sum, f) => sum + (f.proteinG || 0), 0),
  };
}

/** A food's badge after the overrides. `primaryConcern` is null when safe. */
export interface FoodScore {
  safetyRating: FoodSafety;
  riskLevel: RiskLevel;
  primaryConcern: string | null;
}

/** Which foods trigger the overrides (aligned with the foods) + label alcohol. */
export interface OverrideFlags {
  alcohol: readonly boolean[];
  energy: readonly boolean[];
  /** A label / barcode ingredient list named alcohol (meal-level). */
  alcoholAdditive: boolean;
}

export interface MealScoring {
  /** The nutrient breakdown BEFORE the overrides (percentages, budgets, levels). */
  base: ScoreBreakdown;
  /** The final headline after the overrides. */
  score: number;
  riskLevel: RiskLevel;
  driver: ScoreDriver;
  /** Aligned with the input foods. */
  foods: FoodScore[];
  /** Indices of the foods that triggered each override. */
  alcoholIndices: number[];
  energyIndices: number[];
}

/** The Liver Score of a food list against daily budgets — exactly the server's Phase 2. */
export function scoreMealFoods(
  foods: readonly ScorableFood[],
  limits: DailyLimits,
  flags: OverrideFlags,
): MealScoring {
  const totals = sumScoredNutrients(foods);
  const base = scoreFromNutrients(
    {
      addedSugarsG: totals.addedSugarsG,
      saturatedFatG: totals.saturatedFatG,
      calories: totals.calories,
      sodiumMg: totals.sodiumMg,
      fiberG: totals.fiberG,
      proteinG: totals.proteinG,
    },
    limits,
  );

  // Per-food badge: the same engine and budgets, so it never contradicts the headline.
  const scored: FoodScore[] = foods.map((f) => {
    const fb = scoreFromNutrients(
      {
        addedSugarsG: f.addedSugarsG || 0,
        saturatedFatG: f.saturatedFatG || 0,
        calories: f.calories || 0,
        sodiumMg: f.sodiumMg || 0,
        fiberG: f.fiberG ?? undefined,
        proteinG: f.proteinG ?? undefined,
      },
      limits,
    );
    const safetyRating = riskToSafety(fb.riskLevel);
    return { safetyRating, riskLevel: fb.riskLevel, primaryConcern: safetyRating === 'safe' ? null : fb.driver };
  });

  let score = base.score;
  let riskLevel: RiskLevel = base.riskLevel;
  let driver: ScoreDriver = base.driver;

  // Energy drink: badge 'safe' → 'moderate' ('caffeine'); the headline floor
  // only when it actually raises the score (a sugared drink keeps 'addedSugar').
  const energyIndices: number[] = [];
  foods.forEach((_, i) => {
    if (flags.energy[i] === true) energyIndices.push(i);
  });
  if (energyIndices.length > 0) {
    for (const i of energyIndices) {
      if (scored[i].safetyRating === 'safe') {
        scored[i] = { safetyRating: 'moderate', riskLevel: 'moderate', primaryConcern: 'caffeine' };
      }
    }
    if (base.score < ENERGY_DRINK_SCORE_FLOOR) {
      score = ENERGY_DRINK_SCORE_FLOOR;
      riskLevel = 'moderate';
      driver = 'caffeine';
    }
  }

  // Alcohol: floors at 90 whatever the nutrients say; badge 'limit' ('alcohol').
  const alcoholIndices: number[] = [];
  foods.forEach((_, i) => {
    if (flags.alcohol[i] === true) alcoholIndices.push(i);
  });
  if (alcoholIndices.length > 0 || flags.alcoholAdditive) {
    for (const i of alcoholIndices) {
      scored[i] = { safetyRating: 'limit', riskLevel: 'high', primaryConcern: 'alcohol' };
    }
    score = Math.max(base.score, ALCOHOL_SCORE_FLOOR);
    riskLevel = 'high';
    driver = 'alcohol';
  }

  return { base, score, riskLevel, driver, foods: scored, alcoholIndices, energyIndices };
}

// ── Score context: replaying a score after a food edit ──────────────────────

/*
 * The server stamps `scoreContext` on every meal / food / label / barcode
 * analysis: the RESOLVED inputs the score was computed against (the daily
 * budgets in force at scan time — not the profile — and which foods tripped
 * the overrides). A client re-scores an edited meal from it, so an edit is
 * judged by exactly the budgets and override decisions of the original scan.
 *
 * Identity: the server assigns `key` ('f0', 'f1', …) to every identified food
 * and every mapper carries it; keys are never recreated from array positions.
 * Replay runs only when the version and basis are known, every food has a
 * unique non-empty key, the stored foods still add up to the stored parent
 * totals, and replaying the unedited foods reproduces the stored score. Any
 * failure is FAIL-CLOSED: the stored score is kept and marked stale
 * ("from the original scan").
 *
 * Overrides stay with the food: the alcohol / energy-drink floor stays while
 * any food whose key is listed remains (a renamed beer is still a beer), and
 * lifts only when that exact food is removed. A food whose (new) name matches
 * a pattern triggers it too. Label alcohol (ingredient list) is meal-level and
 * stays until a fresh analysis.
 */

export const SCORE_CONTEXT_VERSION = 1;
export const SCORE_BASIS = 'rawPerServingFoods-v1';

export interface ScoreContextFlag {
  foodKey: string;
  name: string;
}

export interface ScoreContext {
  v: number;
  basis: string;
  /** The daily budgets the score was computed against (fiber / protein: display only). */
  limits: {
    calories: number;
    addedSugarG: number;
    saturatedFatG: number;
    sodiumMg: number;
    fiberG: number | null;
    proteinG: number | null;
  };
  proteinIsTarget: boolean;
  limitsSource: LimitSource;
  /** Foods that tripped the alcohol override (by name or the model's flag). */
  alcohol: ScoreContextFlag[];
  /** Foods that tripped the energy-drink override (by name or the model's flag). */
  energy: ScoreContextFlag[];
  /** The label's ingredient list named alcohol. */
  alcoholAdditive: boolean;
}

/** Server: the identity key of the i-th identified food. */
export function foodKeyAt(index: number): string {
  return `f${index}`;
}

const LIMIT_SOURCES: readonly string[] = ['calculatedLimits', 'prevention', 'fattyLiverDefault'];
const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const isBool = (b: unknown): b is boolean => typeof b === 'boolean';

/** Server: the context for an analysis scored against `limits` (+ source). */
export function buildScoreContext(
  limits: DailyLimits & { source: LimitSource },
  alcohol: ScoreContextFlag[],
  energy: ScoreContextFlag[],
  alcoholAdditive: boolean,
): ScoreContext {
  return {
    v: SCORE_CONTEXT_VERSION,
    basis: SCORE_BASIS,
    limits: {
      calories: limits.calories,
      addedSugarG: limits.addedSugarG,
      saturatedFatG: limits.saturatedFatG,
      sodiumMg: limits.sodiumMg,
      fiberG: limits.fiberG ?? null,
      proteinG: limits.proteinG ?? null,
    },
    proteinIsTarget: limits.proteinIsTarget === true,
    limitsSource: limits.source,
    alcohol: alcohol.map((x) => ({ foodKey: x.foodKey, name: x.name })),
    energy: energy.map((x) => ({ foodKey: x.foodKey, name: x.name })),
    alcoholAdditive,
  };
}

function parseFlags(raw: unknown): ScoreContextFlag[] | null {
  if (!Array.isArray(raw)) return null;
  const out: ScoreContextFlag[] = [];
  for (const x of raw) {
    if (!x || typeof x.foodKey !== 'string' || !x.foodKey) return null;
    out.push({ foodKey: x.foodKey, name: typeof x.name === 'string' ? x.name : '' });
  }
  return out;
}

/** A stored context this client can replay, or null (unknown version / basis / damaged shape). */
export function parseScoreContext(raw: unknown): ScoreContext | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as Record<string, any>;
  if (c.v !== SCORE_CONTEXT_VERSION || c.basis !== SCORE_BASIS) return null;
  const l = c.limits;
  if (!l || !isNum(l.calories) || !isNum(l.addedSugarG) || !isNum(l.saturatedFatG) || !isNum(l.sodiumMg)) return null;
  if (!(l.fiberG === null || isNum(l.fiberG)) || !(l.proteinG === null || isNum(l.proteinG))) return null;
  if (!LIMIT_SOURCES.includes(c.limitsSource)) return null;
  if (!isBool(c.proteinIsTarget) || !isBool(c.alcoholAdditive)) return null;
  const alcohol = parseFlags(c.alcohol);
  const energy = parseFlags(c.energy);
  if (!alcohol || !energy) return null;
  return {
    v: SCORE_CONTEXT_VERSION,
    basis: SCORE_BASIS,
    limits: {
      calories: l.calories,
      addedSugarG: l.addedSugarG,
      saturatedFatG: l.saturatedFatG,
      sodiumMg: l.sodiumMg,
      fiberG: l.fiberG,
      proteinG: l.proteinG,
    },
    proteinIsTarget: c.proteinIsTarget,
    limitsSource: c.limitsSource,
    alcohol,
    energy,
    alcoholAdditive: c.alcoholAdditive,
  };
}

/** The daily budgets frozen in a context. */
export function limitsFromScoreContext(ctx: ScoreContext): DailyLimits {
  return {
    calories: ctx.limits.calories,
    addedSugarG: ctx.limits.addedSugarG,
    saturatedFatG: ctx.limits.saturatedFatG,
    sodiumMg: ctx.limits.sodiumMg,
    fiberG: ctx.limits.fiberG ?? undefined,
    proteinG: ctx.limits.proteinG ?? undefined,
    proteinIsTarget: ctx.proteinIsTarget,
  };
}

/** A food as the replay sees it: per-serving nutrients + identity. */
export interface RescoreFood extends ScorableFood {
  key?: string | null;
}

/** Every food has a unique, non-empty key (and there is at least one food). */
export function hasUsableFoodKeys(foods: readonly RescoreFood[]): boolean {
  if (foods.length === 0) return false;
  const seen = new Set<string>();
  for (const f of foods) {
    if (typeof f.key !== 'string' || !f.key || seen.has(f.key)) return false;
    seen.add(f.key);
  }
  return true;
}

/** The scored keys and their display rounding: kcal / mg to whole numbers, g to one decimal. */
const SCORED_ROUNDING = {
  calories: 0.5,
  addedSugarsG: 0.05,
  saturatedFatG: 0.05,
  sodiumMg: 0.5,
} as const;

type ScoredKey = keyof typeof SCORED_ROUNDING;

/**
 * Basis consistency: the per-serving foods must still add up to the stored
 * parent totals on every SCORED nutrient. `parent` holds the totals for
 * `servings` servings (a diary entry: its nutrients + servingsConsumed; a saved
 * food or a fresh analysis: its per-serving totals + 1). The tolerance is
 * display rounding — one rounding of the per-serving value (scaled by
 * servings) plus one of the parent itself, doubled for a saved food re-logged
 * from a rounded entry. Catches a totals-only edit (the entry's nutrient form)
 * that changed the parent but kept a valid-looking context.
 */
export function basisMatches(
  foods: readonly ScorableFood[],
  parent: Partial<Record<ScoredKey, number | null>> | null | undefined,
  servings: number,
): boolean {
  if (!parent || !isNum(servings) || servings <= 0) return false;
  const sum = sumScoredNutrients(foods);
  for (const key of Object.keys(SCORED_ROUNDING) as ScoredKey[]) {
    const stored = parent[key];
    if (!isNum(stored)) return false;
    const tolerance = SCORED_ROUNDING[key] * (2 + 2 * servings) + 1e-9;
    if (Math.abs(sum[key] * servings - stored) > tolerance) return false;
  }
  return true;
}

/** The meal-level score fields a rescore rebuilds (as the server's Phase 2 would). */
export interface AnalysisScoreBreakdown {
  score: number;
  riskLevel: RiskLevel;
  driver: ScoreDriver;
  percentages: ScoreBreakdown['percentages'];
  dailyLimits: ScoreBreakdown['dailyLimits'];
  levels: ScoreBreakdown['levels'];
  limitsSource: LimitSource;
}

export interface RescoreResult {
  impactScore: number;
  overallRisk: RiskLevel;
  levels: ScoreBreakdown['levels'];
  scoreBreakdown: AnalysisScoreBreakdown;
  /** Aligned with the input foods. */
  foods: FoodScore[];
  /** Names of the alcoholic foods still present (empty when none). */
  alcoholFoods: string[];
  /** The context carried forward: same budgets, override lists rebuilt from the current foods. */
  scoreContext: ScoreContext;
}

/**
 * Score a food list against a stored context — exactly what the server's
 * Phase 2 computes for the same foods and budgets. Null when identity is unusable.
 *
 * `matchNames: false` replays the overrides from the context's food keys alone
 * — the stored decision. Acceptance uses it, so an unedited meal still
 * replays to its stored score after a later release changes the name patterns
 * (or on a client with an older pattern list).
 */
export function rescoreFoods(
  foods: readonly RescoreFood[],
  ctx: ScoreContext,
  opts?: { matchNames?: boolean },
): RescoreResult | null {
  if (!hasUsableFoodKeys(foods)) return null;
  const matchNames = opts?.matchNames !== false;
  const alcoholKeys = new Set(ctx.alcohol.map((x) => x.foodKey));
  const energyKeys = new Set(ctx.energy.map((x) => x.foodKey));
  const alcohol = foods.map((f) => alcoholKeys.has(f.key as string) || (matchNames && nameLooksAlcoholic(f.name)));
  const energy = foods.map((f) => energyKeys.has(f.key as string) || (matchNames && nameLooksLikeEnergyDrink(f.name)));
  const m = scoreMealFoods(foods, limitsFromScoreContext(ctx), {
    alcohol,
    energy,
    alcoholAdditive: ctx.alcoholAdditive,
  });
  const flagged = (list: boolean[]): ScoreContextFlag[] =>
    foods.filter((_, i) => list[i]).map((f) => ({ foodKey: f.key as string, name: f.name || '' }));
  return {
    impactScore: m.score,
    overallRisk: m.riskLevel,
    levels: m.base.levels,
    scoreBreakdown: {
      score: m.score,
      riskLevel: m.riskLevel,
      driver: m.driver,
      percentages: m.base.percentages,
      dailyLimits: m.base.dailyLimits,
      levels: m.base.levels,
      limitsSource: ctx.limitsSource,
    },
    foods: m.foods,
    alcoholFoods: m.alcoholIndices.map((i) => foods[i].name || ''),
    scoreContext: { ...ctx, alcohol: flagged(alcohol), energy: flagged(energy) },
  };
}

/** Why a stored context was not accepted for replay. */
export type ScoreContextRejection = 'none' | 'version' | 'keys' | 'basis' | 'replay';

export type ScoreContextAcceptance =
  | { ok: true; context: ScoreContext }
  | { ok: false; reason: ScoreContextRejection };

/**
 * Accept a stored context for replay only when everything still lines up:
 * known version + basis, unique non-empty keys on the stored foods, foods that
 * add up to the stored parent totals, and a no-edit replay that reproduces the
 * stored score (so an older client that changed the foods without re-scoring
 * is caught too).
 */
export function acceptScoreContext(input: {
  scoreContext: unknown;
  storedScore: number | null | undefined;
  foods: readonly RescoreFood[];
  parent: Partial<Record<ScoredKey, number | null>> | null | undefined;
  servings: number;
}): ScoreContextAcceptance {
  if (input.scoreContext == null) return { ok: false, reason: 'none' };
  const context = parseScoreContext(input.scoreContext);
  if (!context) return { ok: false, reason: 'version' };
  if (!hasUsableFoodKeys(input.foods)) return { ok: false, reason: 'keys' };
  if (!basisMatches(input.foods, input.parent, input.servings)) return { ok: false, reason: 'basis' };
  const replay = rescoreFoods(input.foods, context, { matchNames: false });
  if (!replay || (input.storedScore != null && replay.impactScore !== input.storedScore)) {
    return { ok: false, reason: 'replay' };
  }
  return { ok: true, context };
}

/** What a food edit means for the score. Servings are never part of it (the score is per serving). */
function scoringSignature(foods: readonly RescoreFood[]): string {
  return JSON.stringify(
    foods.map((f) => [
      f.key ?? null,
      f.name ?? '',
      f.calories || 0,
      f.addedSugarsG || 0,
      f.saturatedFatG || 0,
      f.sodiumMg || 0,
      f.fiberG ?? null,
      f.proteinG ?? null,
    ]),
  );
}

export type FoodEditScoring =
  /** Nothing scoring-relevant changed: keep the stored analysis as it is. */
  | { kind: 'unchanged'; acceptance: ScoreContextAcceptance }
  /** Re-scored from the stored context. */
  | { kind: 'rescored'; result: RescoreResult }
  /** Legacy / unusable context: keep the stored score, mark it stale. */
  | { kind: 'stale'; reason: ScoreContextRejection };

/**
 * The one decision every editor makes after a food edit: compare the edited
 * foods with the stored ones, and re-score only through an accepted context.
 */
export function scoreFoodEdit(input: {
  scoreContext: unknown;
  storedScore: number | null | undefined;
  originalFoods: readonly RescoreFood[];
  editedFoods: readonly RescoreFood[];
  parent: Partial<Record<ScoredKey, number | null>> | null | undefined;
  servings: number;
}): FoodEditScoring {
  const acceptance = acceptScoreContext({
    scoreContext: input.scoreContext,
    storedScore: input.storedScore,
    foods: input.originalFoods,
    parent: input.parent,
    servings: input.servings,
  });
  if (scoringSignature(input.originalFoods) === scoringSignature(input.editedFoods)) {
    return { kind: 'unchanged', acceptance };
  }
  if (!acceptance.ok) return { kind: 'stale', reason: acceptance.reason };
  const result = input.editedFoods.length ? rescoreFoods(input.editedFoods, acceptance.context) : null;
  if (!result) return { kind: 'stale', reason: 'keys' };
  return { kind: 'rescored', result };
}
